import React from 'react';

/**
 * One segmented control for every "pick a view" switch in Agents 2.0: the agent
 * workspace's Chat · Form · Both, and the Markdown editor's Write · Split ·
 * Preview. A single bordered pill group; the selected segment is raised.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  options: ReadonlyArray<SegmentedOption<T>>;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** `data-testid` prefix: the group gets `${testId}`, each option `${testId}-${value}`. */
  testId?: string;
}

export function SegmentedControl<T extends string>({ value, options, onChange, ariaLabel, testId }: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 rounded-ai-pill border border-ai-border bg-ai-muted p-0.5"
      data-testid={testId}
    >
      {options.map(({ value: v, label, icon: Icon }) => {
        const selected = v === value;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={selected}
            className={`inline-flex h-8 items-center gap-1.5 rounded-ai-pill px-3 text-sm font-medium transition-colors duration-(--ai-duration-fast) ease-ai ${
              selected ? 'bg-ai-surface text-ai-text shadow-sm' : 'text-ai-text-secondary hover:text-ai-text'
            }`}
            data-testid={testId ? `${testId}-${v}` : undefined}
          >
            {Icon && <Icon className="h-4 w-4" />}
            {label}
          </button>
        );
      })}
    </div>
  );
}
